/**
 * @requires basiclightbox
 */

import * as basicLightbox from 'basiclightbox';

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'videoLightbox', ( config = {}) => ({

		source      : config.source || '',
		youtube     : config.youtube || '',
		vimeo       : config.vimeo || '',
		webm        : config.webm || '',
		mp4         : config.mp4 || '',
		poster      : config.poster || '',
		lightbox : null,
		onKeyDown: null,
		trigger  : null,

		markup() {
			if ( 'youtube' === this.source ) {
				const query = new URLSearchParams({
					autoplay   : '1',
					playsinline: '1',
					rel        : '0',
				}).toString();

				const src = `https://www.youtube-nocookie.com/embed/${ encodeURIComponent( this.youtube ) }?${ query }`;

				return `<div class="video-lightbox__inner video-lightbox__inner--youtube">
					<button class="video-lightbox__close" aria-label="Close video">&#x2715;</button>
					<div class="video-lightbox__frame">
						<iframe src="${ src }" title="Play video" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>
					</div>
				</div>`;
			}

			if ( 'vimeo' === this.source ) {
				// video_id is either "{id}" or "{id}/{hash}" for unlisted videos.
				const [ vimeoId, vimeoHash ] = this.vimeo.split( '/' );
				const params = { autoplay: '1', playsinline: '1' };
				if ( vimeoHash ) params.h = vimeoHash;
				const query = new URLSearchParams( params ).toString();
				const src = `https://player.vimeo.com/video/${ encodeURIComponent( vimeoId ) }?${ query }`;

				return `<div class="video-lightbox__inner video-lightbox__inner--vimeo">
					<button class="video-lightbox__close" aria-label="Close video">&#x2715;</button>
					<div class="video-lightbox__frame">
						<iframe src="${ src }" title="Play video" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>
					</div>
				</div>`;
			}

			return `<div class="video-lightbox__inner">
				<button class="video-lightbox__close" aria-label="Close video">&#x2715;</button>
				<video controls autoplay playsinline poster="${ this.poster }">
					<source src="${ this.webm }" type="video/webm">
					<source src="${ this.mp4 }" type="video/mp4">
				</video>
			</div>`;
		},

		// Focusable elements to trap Tab within, in DOM order. The cross-origin player iframe is
		// deliberately excluded: it swallows keystrokes once focused, so ESC would stop working and the
		// user would be stranded inside it with no obvious way out. The same-origin <video> is included.
		focusable() {
			const root = this.lightbox.element();
			return [ ...root.querySelectorAll( '.video-lightbox__close, video' ) ];
		},

		open() {
			// Remember the element that opened the lightbox so focus can return to it on close.
			this.trigger = this.$el;

			this.lightbox = basicLightbox.create(
				this.markup(),
				{
					onClose: ( instance ) => {
						const video = instance.element().querySelector( 'video' );
						if ( video ) video.pause();

						const iframe = instance.element().querySelector( 'iframe' );
						if ( iframe ) iframe.src = '';

						window.removeEventListener( 'keydown', this.onKeyDown, true );

						// Return focus to the trigger so keyboard users aren't dropped at the top of the page.
						if ( this.trigger ) this.trigger.focus();
					},
				}
			);

			this.onKeyDown = ( event ) => {
				if ( 'Escape' === event.key ) {
					this.close();
					return;
				}

				// Trap Tab so focus cycles within the modal and can never land behind it or in the iframe.
				if ( 'Tab' === event.key ) {
					const items = this.focusable();
					if ( ! items.length ) return;

					const first = items[ 0 ];
					const last  = items[ items.length - 1 ];
					const active = document.activeElement;

					// Any focus outside the trapped set (iframe, page behind) snaps back to the close button.
					if ( ! items.includes( active ) ) {
						event.preventDefault();
						first.focus();
					} else if ( event.shiftKey && active === first ) {
						event.preventDefault();
						last.focus();
					} else if ( ! event.shiftKey && active === last ) {
						event.preventDefault();
						first.focus();
					}
				}
			};

			// Capture phase catches keys before same-origin controls consume them.
			window.addEventListener( 'keydown', this.onKeyDown, true );

			this.lightbox.show();

			const closeButton = this.lightbox.element().querySelector( '.video-lightbox__close' );
			closeButton.addEventListener( 'click', () => this.close() );

			// Move focus into the modal so Space/Enter act on its controls, not the play button that opened it.
			closeButton.focus();
		},

		close() {
			if ( this.lightbox ) this.lightbox.close();
		},

	}) );
});
